import {
  BaseEdge,
  EdgeLabelRenderer,
  getBezierPath,
  type EdgeProps,
} from "@xyflow/react";

import type { LabeledEdgeData } from "./types";

/**
 * Custom bezier edge: colored, solid/dashed, with a mid-edge text label AND a
 * numbered circle badge — the architecture-map.html arrow system (feedback #8b).
 * `getBezierPath` is the xyflow-native equivalent of the reference's `curve()`
 * (horizontal-handle S-bezier). When a flow is active the edge gets a white-halo
 * text label (the reference's `.elabel`) nudged above the midpoint, plus a black
 * (`--primary`) circle badge carrying the per-flow sequence number (`d.seq`) at
 * the midpoint. Both are hidden on the full map, matching the reference, which
 * shows only faint grey wires with no labels to avoid clutter.
 */
export function LabeledEdge({
  id,
  sourceX,
  sourceY,
  sourcePosition,
  targetX,
  targetY,
  targetPosition,
  markerEnd,
  data,
}: EdgeProps) {
  const d = (data ?? {}) as LabeledEdgeData;
  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });

  const color = d.color ?? "hsl(var(--border))";
  const strokeWidth = d.active ? 2 : d.dimmed ? 1.1 : 1.3;
  const opacity = d.dimmed ? 0.06 : d.active ? 1 : 0.55;

  return (
    <>
      <BaseEdge
        id={id}
        path={edgePath}
        markerEnd={markerEnd}
        style={{
          stroke: color,
          strokeWidth,
          opacity,
          strokeDasharray: d.dashed ? "5 4" : undefined,
        }}
      />
      {d.active ? (
        <EdgeLabelRenderer>
          {/* text label — nudged above the badge, white-halo via stroke-like
              bg + ring so it reads over wires (reference `.elabel`). */}
          {d.label ? (
            <div
              className="nodrag nopan pointer-events-none absolute whitespace-nowrap rounded bg-background/90 px-1 text-[9.5px] font-semibold text-foreground"
              style={{
                transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY - 14}px)`,
              }}
            >
              {d.label}
            </div>
          ) : null}
          {/* numbered circle badge at the midpoint (BLACK accent). */}
          {d.seq != null ? (
            <div
              className="nodrag nopan pointer-events-none absolute flex h-[18px] w-[18px] items-center justify-center rounded-full text-[10px] font-bold leading-none"
              style={{
                transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
                background: "hsl(var(--primary))",
                color: "hsl(var(--primary-foreground))",
                boxShadow: "0 0 0 1px hsl(var(--background))",
              }}
            >
              {d.seq}
            </div>
          ) : null}
        </EdgeLabelRenderer>
      ) : null}
    </>
  );
}
